import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ClientCombobox from './ClientCombobox';

// Diálogo compartido (Taller / Turnos) para completar los datos de un cliente
// "Sin datos" (creado solo con la patente) o corregir nombre/teléfono de uno real.
// Si el auto en realidad es de un cliente ya cargado, permite fusionar el
// placeholder en ese cliente (repunta turnos, remitos y vehículos).

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string | null;
  onSaved?: () => void;
  // Escape opcional (ej. cobro en Remitos): seguir sin completar los datos
  skipLabel?: string;
  onSkip?: () => void;
}

const clientName = (c: any) =>
  c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'Sin nombre';

export default function ClientDataDialog({ open, onOpenChange, clientId, onSaved, skipLabel, onSkip }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState<any>(null);
  const [mode, setMode] = useState<'datos' | 'existente'>('datos');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [allClients, setAllClients] = useState<any[]>([]);
  const [target, setTarget] = useState<any>(null);

  const isPlaceholder = Number(client?.isPlaceholder) === 1;

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    setClient(null);
    setMode('datos');
    setTarget(null);
    axios.get(`/api/clients/${clientId}`)
      .then((r) => {
        const c = r.data?.client || r.data;
        setClient(c);
        const ph = Number(c?.isPlaceholder) === 1;
        setFirstName(ph ? '' : (c?.firstName || ''));
        setLastName(ph ? '' : (c?.lastName || ''));
        setPhone(c?.phone || '');
      })
      .catch(() => toast.error('No se pudo cargar el cliente'))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  // Lista para "ya es cliente": solo cuando se entra a ese modo
  useEffect(() => {
    if (!open || mode !== 'existente' || allClients.length > 0) return;
    axios.get('/api/clients?limit=10000')
      .then((r) => {
        const list = (r.data?.clients || r.data || []).filter(
          (c: any) => Number(c.isPlaceholder) !== 1 && c.id !== clientId && c.id !== 'consumidor-final'
        );
        setAllClients(list);
      })
      .catch(() => toast.error('No se pudieron cargar los clientes'));
  }, [open, mode, allClients.length, clientId]);

  const saveData = async () => {
    if (!firstName.trim() || !phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    try {
      setSaving(true);
      await axios.put(`/api/clients/${clientId}`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        isPlaceholder: 0
      });
      toast.success('Datos del cliente guardados');
      // onSaved ANTES de cerrar: quien nos usa como gate (cobro en Remitos)
      // resuelve su promesa acá y el cierre ya no la pisa
      onSaved?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudieron guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const mergeInto = async () => {
    if (!target) return;
    try {
      setSaving(true);
      await axios.post(`/api/clients/${clientId}/merge-into`, { targetId: target.id });
      toast.success(`Vinculado a ${clientName(target)}: turnos, remitos y vehículos pasaron a su ficha`);
      onSaved?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo vincular al cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Datos del cliente</DialogTitle>
          <DialogDescription>
            {isPlaceholder
              ? 'Este cliente se creó solo con la patente. Completá sus datos o, si ya estaba cargado en el sistema, vinculalo a su ficha.'
              : 'Completar o corregir los datos de contacto.'}
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-muted-foreground py-4">Cargando...</p>}

        {!loading && client && (
          <>
            {isPlaceholder && (
              <div className="grid grid-cols-2 gap-2">
                <Button variant={mode === 'datos' ? 'default' : 'outline'} className="h-10" onClick={() => setMode('datos')}>
                  Cargar datos
                </Button>
                <Button variant={mode === 'existente' ? 'default' : 'outline'} className="h-10" onClick={() => setMode('existente')}>
                  Ya es cliente
                </Button>
              </div>
            )}

            {mode === 'datos' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Nombre *</Label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Nombre" autoFocus />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Apellido</Label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Apellido" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Teléfono *</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ej: 3364123456" inputMode="tel" />
                </div>
              </div>
            )}

            {mode === 'existente' && (
              <div className="space-y-3">
                <ClientCombobox
                  clients={allClients}
                  selectedClient={target}
                  onSelect={setTarget}
                  getClientName={clientName}
                  placeholder="Buscar el cliente por nombre o teléfono..."
                />
                {target && (
                  <p className="text-xs text-muted-foreground">
                    El vehículo, los turnos y los remitos de este "Sin datos" pasan a la ficha de{' '}
                    <span className="font-semibold text-foreground">{clientName(target)}</span>. No se puede deshacer.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {isPlaceholder && skipLabel && onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="text-left text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            {skipLabel}
          </button>
        )}

        <DialogFooter>
          <Button variant="outline" className="h-10" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {mode === 'datos' ? (
            <Button className="h-10" onClick={saveData} disabled={saving || loading || !client}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          ) : (
            <Button className="h-10" onClick={mergeInto} disabled={saving || !target}>
              {saving ? 'Vinculando...' : 'Vincular'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
